import { useMemo } from 'react';
import { t } from '@lingui/macro';
import { isEmpty, includes, map } from 'lodash';
import { useDataService } from './ApiDataService';
import useUiToasts, { createToastConfig, INFO } from '../stores/ToastStore';
import { useIsNg1UniversalMonitorAllowed, useIsNg1User } from '../stores/SessionStore';

import { getLogger } from '../utilities/UseLog';

// To enable log debug output, type `setLogLevel('Ng1DrillDownService', LOG_LEVELS.DEBUG)` in the browser
// console.
const log = getLogger('Ng1DrillDownService');

/*
Keep a global nG1 window reference. It is shared by nGP modules,
like test detail and test log page
*/
let nG1Window = null;
let nG1WinName = null; // needs to match what nG1 is using, and number characters seem to cause problems
let callBack = null;

const nG1SessionLogoutToastMessage = t`Your nGeniusONE session has expired.  Please log in again.`;

function onNG1Message(event) {
    // This is pretty verbose, so I'm using trace instead of debug so that it doesn't swamp other debug
    // output.
    log.trace(`Received message from nG1: ${JSON.stringify(event.data)}`);

    if (callBack) {
        callBack(event.data);
        callBack = null;
    }
}

// setup postMessage listener
window.addEventListener('message', onNG1Message, false);

const fiveMinutes = 5 * 60 * 1000;

export default function useNg1DrillDownService() {
    const dataService = useDataService();
    const [, uiToastActions] = useUiToasts();
    const [isNg1User] = useIsNg1User();
    const [isNg1UniversalMonitorAllowed] = useIsNg1UniversalMonitorAllowed();
    const service = useMemo(
        () => factory(dataService, uiToastActions, isNg1User, isNg1UniversalMonitorAllowed),
        [dataService, uiToastActions, isNg1User, isNg1UniversalMonitorAllowed]
    );

    return service;
}

function factory(dataService, uiToastActions, isNg1User, isNg1UniversalMonitorAllowed) {
    return {
        hasNg1DrillDownPrivilege,
        isTestDrillDownAllowed,
        isNpointDrillDownAllowed,
        drillDown,
    };

    function isTestDrillDownAllowed(test, agent) {
        return hasNg1DrillDownPrivilege() && test?.beaconPackets && isTrafficOn(agent, test);
    }

    function isTrafficOn(agent, test) {
        const callers = test?.testConfig?.caller?.ids;
        if (isEmpty(callers)) {
            return true;
        }
        const callerIds = map(callers, 'id');
        return includes(callerIds, agent?.uuid);
    }

    function isNpointDrillDownAllowed(nPoint) {
        return hasNg1DrillDownPrivilege() && nPoint?.beaconPackets;
    }

    function hasNg1DrillDownPrivilege() {
        return isNg1User && isNg1UniversalMonitorAllowed;
    }

    async function drillDown(timeRange, test, nPoint) {
        let startTime = roundTo5MinBoundary(timeRange?.start);
        const endTime = roundTo5MinBoundary(timeRange?.end);
        if (startTime === endTime) {
            startTime -= fiveMinutes;
        }
        const testLocationKeyId = test?.locationKeyId;
        const nPointLocationKeyId = nPoint?.locationKeyId;
        const nG1Token = await dataService.get('/ipm/ng1/ng1-server-token');

        if (isEmpty(nG1Token?.tempToken)) {
            uiToastActions.createToast(createToastConfig(INFO, nG1SessionLogoutToastMessage));
            dataService.post('/ipm/auth/logout', {});
            // '/login' is for that nGP restarts or unexpected exceptions reported;
            // 'ng1Url' is for nG1 launched by nG1 has no password when its session timeout.
            const redirect = nG1Token.ng1Url ? nG1Token.ng1Url : '/login';
            // eslint-disable-next-line react-portal/no-window-location-assign
            setTimeout(() => window.location.assign(redirect), 5000);
            return;
        }

        if (window.top.opener) {
            log.debug('Launched by nG1 window');
            nG1Window = window.top.opener;
        }

        focusWindow();

        const alive = await isnG1Alive();

        if (!alive) {
            nG1Window = launchNG1(nG1Token, startTime, endTime, testLocationKeyId, nPointLocationKeyId);
        } else {
            messageToNg1(nG1Token, startTime, endTime, testLocationKeyId, nPointLocationKeyId);
        }
    }

    function roundTo5MinBoundary(time) {
        return Math.round(time / fiveMinutes) * fiveMinutes;
    }

    function launchNG1(nG1Token, startTime, endTime, testLocationKeyId, nPointLocationKeyId) {
        // spaces in parameter should be replaced as '+'
        let ng1Url = `${nG1Token?.ng1Url}/common/SecureBanner.jsp?source=/console?tempTokenId=${nG1Token?.tempToken}`;
        const monitorID = `com.netscout.ngeniusconsole.shell.ADM`;
        ng1Url += `&modID=${monitorID}&modMsg=params&urlLaunch=1&version=2&monitor=UNIVERSAL&launchedFrom=NGPULSE`;
        ng1Url += `&startTime=${startTime}&endTime=${endTime}`;

        if (testLocationKeyId) {
            ng1Url += `&testId=${testLocationKeyId}`;
        }

        if (nPointLocationKeyId) {
            ng1Url += `&nPointId=${nPointLocationKeyId}`;
        }

        if (!nG1WinName) {
            nG1WinName = `nG1Win_${nG1Token?.tempToken}`;
        }

        log.debug(`Opening nG1 window ${nG1WinName} with URL: ${ng1Url}`);

        return window.open(ng1Url, nG1WinName);
    }

    function messageToNg1(nG1Token, startTime, endTime, testLocationKeyId, nPointLocationKeyId) {
        const monitorID = `com.netscout.ngeniusconsole.shell.ADM`;
        const drillDownData = {
            urlLaunch: 1,
            parameters: null,
            version: 2,
            monitor: 'UNIVERSAL',
            launchedFrom: 'NGPULSE',
            tempTokenId: nG1Token?.tempToken,
            startTime,
            endTime,
        };

        if (testLocationKeyId) {
            drillDownData.testId = testLocationKeyId;
        }

        if (nPointLocationKeyId) {
            drillDownData.nPointId = nPointLocationKeyId;
        }

        const drillDownDataStr = JSON.stringify(drillDownData);
        const msg = JSON.stringify({
            origin: 'pulse',
            destination: monitorID,
            data: drillDownDataStr,
            subDestn: null,
            gvFocus: true,
        });

        nG1Window.postMessage(msg, '*');

        log.debug(`Sent nG1 message: ${msg}`);
    }

    function isnG1Alive() {
        return new Promise((resolve) => {
            log.debug(`Checking nG1 window state...`);

            if (!nG1Window || nG1Window.closed) return resolve(false);

            if (window.top.opener) {
                log.debug(`nG1 window is active`);
                return resolve(true);
            }

            // send message to wrapper
            callBack = (data) => {
                const alive = data === 'TRUE';
                log.debug(`nG1 window active: ${data}`);
                resolve(alive);
            };

            nG1Window.postMessage('ISALIVE', '*');
        });
    }

    function focusWindow() {
        window.blur(); // for Firefox
        if (nG1Window) {
            nG1Window.focus(); // for Firefox
        }
    }
}
